/**
 * UI 文言の辞書（日本語/英語・要件20）。
 *
 * 各キーは文字列、または引数を取る関数（動的文言）。`t(lang, dict)` で現在言語の
 * 値を引く。文言はコンポーネント横断で1ファイルに集約し、ja/en を対で管理する。
 *
 * 使い方:
 *   const m = t(lang, MESSAGES);
 *   m.post.title                // 文字列
 *   m.participant.header(3)     // 関数（動的）
 */
import type { Lang } from './lang';

/** ja/en を対で持つ辞書オブジェクトから、現在言語の面を取り出す。 */
export function t<T>(lang: Lang, dict: { ja: T; en: T }): T {
  return dict[lang];
}

export const MESSAGES = {
  ja: {
    // 地図右上ツールバー（MapToolbar）のアイコン列のラベル。
    toolbar: { label: '地図の設定' },
    // Web 版のヘルプ（タスク50）。数値は実装の定数と一致させる
    // （位置60分=POINT_TTL_SEC・軌跡30分=TRAIL_WINDOW_SEC・ステータス3時間・投稿30日・トラッキング30秒）。
    help: {
      button: 'ヘルプ',
      title: '使い方',
      tabs: { map: '地図の見方', join: '参加する', privacy: 'プライバシー' },
      legend: [
        { key: 'participant', name: '参加者', desc: '色が濃いほど最近の更新' },
        { key: 'pulse', name: '2分以内に更新', desc: 'いま動いている人' },
        { key: 'self', name: 'あなた', desc: '自分のピン（オレンジの縁）' },
        { key: 'trail', name: '軌跡', desc: '直近30分の移動' },
        { key: 'cluster', name: '参加者のまとまり', desc: 'タップで拡大' },
        { key: 'status', name: 'ステータス', desc: 'その人のひとこと（3時間で消える）' },
        { key: 'venue', name: '会場', desc: 'タップで公式セッションカタログ（要ログイン）' },
        { key: 'post', name: '投稿', desc: 'タップで中身。写真・動画はサムネイルで表示' },
        { key: 'postCluster', name: '投稿のまとまり', desc: 'タップで拡大・一覧' },
      ],
      steps: [
        '左下の「ログイン」から Google アカウントでログイン',
        '「📍 チェックイン」で今いる場所・会場を1回送る／「📡 トラッキング」で画面を開いている間、30秒ごとに送る',
        '「💬 ステータス」で一言、「📝 投稿」で写真・動画・文章をピン留め',
        '公開の設定はチェックインの画面（「地図に表示する」「軌跡を表示する」）、名前・アイコンは「⚙️ プロフィール」',
      ],
      telegram: 'Telegram で参加する場合は、主催者から招待を受けてください。',
      privacyHeader: { item: '項目', period: '表示される期間' },
      privacyRows: [
        { label: '位置（ピン）', value: '最後の更新から60分' },
        { label: '軌跡', value: '直近30分' },
        { label: 'ステータス', value: '3時間' },
        { label: '投稿', value: '30日' },
        { label: 'トラッキング', value: '画面を開いている間（30秒ごと）' },
      ],
      privacyNotes: [
        'この地図はログインしなくても誰でも見られます。',
        '「地図に表示する」をオフにすると、位置を送っても地図には表示されません。',
      ],
    },
    lang: { toggleTitle: '言語を切り替え', ja: '日本語', en: 'English' },
    // マップスタイル切替（要件19）。Hybrid は利用者から見れば「衛星」なので satellite と呼ぶ。
    mapStyle: {
      toggleTitle: '地図の見た目を切り替え',
      standard: '標準',
      monochrome: 'モノクロ',
      satellite: '衛星',
    },
    common: { close: '閉じる', cancel: 'キャンセル' },
    venue: {
      catalogLink: 'この会場のセッションを公式カタログで見る',
      catalogNote: '閲覧には re:Invent への参加登録とログインが必要です',
    },
    app: { preparing: '地図を準備中…' },
    participant: {
      loading: '読み込み中…',
      error: '取得エラー',
      header: (n: number) => `公開中: ${n}人`,
    },
    // ライブアクティビティフィード（タスク54）。名前（太字）の後に続く文言。
    feed: {
      label: 'アクティビティフィード',
      on: 'オン',
      off: 'オフ',
      checkin: (venue?: string) =>
        venue ? `が ${venue} にチェックイン` : 'がチェックイン',
      posted: 'が投稿しました',
      move: 'が移動中',
      aggCount: (n: number) => `${n}人`,
      aggSuffix: 'が移動中',
      near: (venue: string) => `${venue} 付近`,
      venueSep: '・',
      othersAfter: (venues: string) => `${venues} ほか`,
    },
    login: {
      trigger: 'ログイン',
      title: 'ログイン',
      desc: 'Google アカウントでログインすると、Web から位置やポストを投稿できます。閲覧だけならログインは不要です。',
      google: 'Google で続ける',
    },
    menu: { logout: 'ログアウト', trackingBadge: '位置を共有中（トラッキング）' },
    tracking: {
      trigger: '📡 トラッキング',
      triggerActive: '📡 トラッキング中',
      title: 'トラッキング',
      note: '画面を開いている間だけ、30秒ごとに現在地を送って地図のピンを動かします。画面を閉じる・別のアプリに切り替えると止まります（戻ると再開）。電池を多く使います。',
      durationLabel: '共有する時間',
      durations: {
        '15m': '15分',
        '1h': '1時間',
        '8h': '8時間',
        indefinite: '停止するまで',
      },
      start: '開始',
      stop: '停止',
      sharing: (remaining: string) => `位置を共有中（${remaining}）`,
      indefinite: '停止するまで',
      remaining: (sec: number) => {
        const min = Math.floor(sec / 60);
        if (min < 1) {
          return 'まもなく終了';
        }
        const h = Math.floor(min / 60);
        return h > 0 ? `残り${h}時間${min % 60}分` : `残り${min}分`;
      },
      lastSent: (time: string) => `最終送信: ${time}`,
      notSentYet: '現在地を取得中…',
      needPermission:
        'トラッキングには位置情報の許可が必要です。アドレスバー左のアイコン →「サイトの設定」→「位置情報」を「許可」にしてください。許可すると「開始」を押せるようになります。',
      unsupported: 'この端末・ブラウザではトラッキングを使えません。',
      checking: '現在地を確認中…',
      startFailed: {
        denied: '位置情報が許可されなかったため、開始できませんでした。',
        unavailable:
          '現在地を特定できなかったため、開始できませんでした。端末の位置情報サービスがオンか確認してください。',
        timeout: '現在地の取得がタイムアウトしたため、開始できませんでした。もう一度お試しください。',
      },
      hiddenWarning:
        '「地図に表示する」がオフのため、送っても地図には表示されません（チェックインの画面で切り替えられます）。',
      problem: {
        denied:
          '位置情報が許可されていないため停止しました。ブラウザのサイト設定で許可してから、もう一度開始してください。',
        session: 'ログインの有効期限が切れたため停止しました。再度ログインしてから開始してください。',
        network: '送信に失敗しました。次の送信で再試行します。',
        position: '現在地を取得できていません。屋外や窓際で改善することがあります。',
      },
    },
    checkin: {
      trigger: '📍 チェックイン',
      title: 'チェックイン',
      here: '今ここ（現在地）',
      fromVenue: '会場から選ぶ',
      showOnMap: '地図に表示する',
      showTrail: '軌跡を表示する',
      trailNeedsMap: '軌跡は「地図に表示する」がオンのときだけ設定できます。',
      locating: '現在地を取得中…',
      loadingSettings: '公開設定を読み込み中…',
      settingsFailed: '公開設定を読み込めませんでした。開き直してください。',
      done: 'チェックインしました！地図にピンが出ます。',
      doneHidden: 'チェックインしました（「地図に表示する」がオフのため、地図には表示されません）。',
      failed: 'チェックインに失敗しました。時間をおいて再度お試しください。',
      sessionExpired: 'ログインの有効期限が切れました。再度ログインしてください。',
      noGeo: 'この端末では現在地を取得できません。会場を選んでください。',
      denied:
        '位置情報の利用が許可されていません。ブラウザの許可設定を確認するか、会場を選んでください。',
      timeout:
        '現在地の取得がタイムアウトしました。もう一度お試しいただくか、会場を選んでください。',
      geoFailed: '現在地を取得できませんでした。会場を選んでください。',
    },
    profile: {
      trigger: '⚙️ プロフィール',
      title: 'プロフィール編集',
      displayName: '表示名',
      namePlaceholder: '地図に表示する名前',
      hideAvatar: 'アイコン（顔写真）を隠す',
      avatarNote:
        'アイコンは Google の画像のみ使えます（アップロード不可）。隠すとイニシャルの丸で表示されます。',
      save: '保存',
      reset: '初期値に戻す',
      sessionExpired: 'ログインの有効期限が切れました。再度ログインしてください。',
      nameEmpty: '表示名を入力してください。',
      nameTooLong: (max: number) => `表示名は${max}文字以内で入力してください。`,
      saved: 'プロフィールを更新しました。地図に反映されます。',
      saveFailed: '更新に失敗しました。時間をおいて再度お試しください。',
      resetDone: '初期値（ログイン名・画像）に戻しました。',
      resetFailed: '初期化に失敗しました。時間をおいて再度お試しください。',
    },
    status: {
      trigger: '💬 ステータス',
      title: 'ステータス',
      note: 'マップ上のあなたのアバターの上に一言を表示します（3時間で自動的に消えます）。',
      placeholder: '例: ランチなう / セッション待機中',
      show: '表示する',
      clear: '消去する',
      tooLong: (max: number) => `ステータスは${max}文字以内で入力してください。`,
      sessionExpired: 'ログインの有効期限が切れました。再度ログインしてください。',
      updated: 'ステータスを更新しました。マップのアバターの上に表示されます。',
      loading: '読み込み中…',
      remaining: (sec: number) => {
        const min = Math.floor(sec / 60);
        if (min < 1) {
          return 'まもなく消えます';
        }
        const h = Math.floor(min / 60);
        return h > 0 ? `あと${h}時間${min % 60}分で消えます` : `あと${min}分で消えます`;
      },
      cleared: 'ステータスを消去しました。',
      failed: '更新に失敗しました。時間をおいて再度お試しください。',
      needCheckin: 'ステータスを設定するには、先にチェックインしてください。',
    },
    post: {
      trigger: '📝 投稿',
      title: '投稿する',
      note: '今いる場所にピンを立てます（現在地が取れないときは60分以内のチェックイン位置）。本文・画像・動画（150MBまで）で記録を残せます。',
      bodyPlaceholder: 'いまの様子をひとこと',
      addImage: '画像を追加',
      reselectImage: '画像を選び直す',
      addVideo: '動画を追加',
      reselectVideo: '動画を選び直す',
      sizeLimit: '150MBまで',
      removeImage: '画像を外す',
      removeVideo: '動画を外す',
      submit: '投稿する',
      sessionExpired: 'ログインの有効期限が切れました。再度ログインしてください。',
      empty: '本文・画像・動画のいずれかを入力してください。',
      bodyTooLong: (max: number) => `本文は${max}文字以内で入力してください。`,
      videoTooLarge:
        '動画が大きすぎます（上限150MB）。4K で撮った場合は短く切るか、1080p で撮ってください。',
      locating: '現在地を取得中…',
      postAtCurrent: '📍 現在地に投稿します',
      postAtCurrentNear: (venue: string) => `📍 現在地（${venue}付近）に投稿します`,
      postAtCheckin: '📍 最新のチェックイン位置に投稿します',
      postAtCheckinNear: (venue: string) =>
        `📍 最新のチェックイン位置（${venue}付近）に投稿します`,
      imageLoadFailed: '画像を読み込めませんでした。別の画像でお試しください。',
      uploading: '動画をアップロード中…',
      posted: '投稿しました！マップにピンが追加されます。',
      needLocation:
        '投稿には現在地か、60分以内のチェックイン位置が必要です。「現在地を取り直す」を押すか、先にチェックインしてください。',
      locDenied:
        '位置情報がブロックされています。アドレスバー左のアイコン →「サイトの設定」→「位置情報」を「許可」にしてください。許可すると自動で取り直します（反映されない場合はページを再読み込みしてください）。',
      locStillDenied: 'まだ位置情報がブロックされています。先にサイトの設定で「許可」にしてください。',
      locRetryFailed: 'もう一度試しましたが、現在地を取得できませんでした。',
      locRetrying: '取得中…',
      reloadPage: 'ページを再読み込み',
      reloadNote: '※ 再読み込みすると、入力中の本文・画像・動画は消えます。',
      locUnavailable:
        '現在地を特定できませんでした。端末の位置情報サービス（Windows の「位置情報」など）がオンか確認してください。',
      locTimeout: '現在地の取得がタイムアウトしました。もう一度お試しください。',
      locUnsupported: 'この端末では現在地を取得できません。',
      retryLocation: '現在地を取り直す',
      failed: '投稿に失敗しました。時間をおいて再度お試しください。',
    },
    postPopup: {
      imageAlt: '投稿画像',
      delete: 'この投稿を削除',
      backToList: '← この場所の一覧へ戻る',
      confirmDelete: 'この投稿を削除しますか？この操作は取り消せません。',
      spotTitle: (n: number) => `この場所の投稿 ${n}件`,
      shareX: 'X で共有',
      shareFacebook: 'Facebook で共有',
      shareLinkedin: 'LinkedIn で共有',
      shareNative: 'その他で共有',
      authorSuffix: (name: string) => `${name}さんの投稿`,
      prevPage: '前へ',
      nextPage: '次へ',
      pageOf: (cur: number, total: number) => `${cur} / ${total}`,
    },
    time: {
      justNow: 'たった今',
      minutesAgo: (n: number) => `${n}分前`,
      hoursAgo: (n: number) => `${n}時間前`,
      daysAgo: (n: number) => `${n}日前`,
    },
  },
  en: {
    toolbar: { label: 'Map settings' },
    help: {
      button: 'Help',
      title: 'How to use',
      tabs: { map: 'Map', join: 'Join', privacy: 'Privacy' },
      legend: [
        { key: 'participant', name: 'Participant', desc: 'Stronger color = more recent update' },
        { key: 'pulse', name: 'Updated within 2 min', desc: 'Someone on the move' },
        { key: 'self', name: 'You', desc: 'Your own pin (orange ring)' },
        { key: 'trail', name: 'Trail', desc: 'Movement in the last 30 minutes' },
        { key: 'cluster', name: 'Group of people', desc: 'Tap to zoom in' },
        { key: 'status', name: 'Status', desc: "That person's short message (clears after 3 hours)" },
        { key: 'venue', name: 'Venue', desc: 'Tap for the official session catalog (sign-in required)' },
        { key: 'post', name: 'Post', desc: 'Tap to open. Photos and videos show a thumbnail' },
        { key: 'postCluster', name: 'Group of posts', desc: 'Tap to zoom in or list' },
      ],
      steps: [
        'Sign in with Google from "Sign in" at the bottom left',
        'Send your location once with "📍 Check in", or every 30 seconds while this page is open with "📡 Tracking"',
        'Share a short message with "💬 Status" and pin photos, videos, or text with "📝 Post"',
        'Visibility is in Check in ("Show on map" / "Show trail"); name and icon are in "⚙️ Profile"',
      ],
      telegram: 'To join via Telegram, get an invitation from the organizer.',
      privacyHeader: { item: 'Item', period: 'Shown for' },
      privacyRows: [
        { label: 'Location (pin)', value: '60 minutes after the last update' },
        { label: 'Trail', value: 'Last 30 minutes' },
        { label: 'Status', value: '3 hours' },
        { label: 'Post', value: '30 days' },
        { label: 'Tracking', value: 'While this page is open (every 30 seconds)' },
      ],
      privacyNotes: [
        'Anyone can view this map without signing in.',
        'With "Show on map" off, your location is not shown on the map even if you send it.',
      ],
    },
    lang: { toggleTitle: 'Switch language', ja: '日本語', en: 'English' },
    mapStyle: {
      toggleTitle: 'Switch map style',
      standard: 'Standard',
      monochrome: 'Mono',
      satellite: 'Satellite',
    },
    common: { close: 'Close', cancel: 'Cancel' },
    venue: {
      catalogLink: "View this venue's sessions in the official catalog",
      catalogNote: 'Requires re:Invent registration and sign-in',
    },
    app: { preparing: 'Preparing map…' },
    participant: {
      loading: 'Loading…',
      error: 'Failed to load',
      header: (n: number) => `Sharing now: ${n}`,
    },
    feed: {
      label: 'Activity feed',
      on: 'On',
      off: 'Off',
      checkin: (venue?: string) =>
        venue ? `checked in at ${venue}` : 'checked in',
      posted: 'posted',
      move: 'is on the move',
      aggCount: (n: number) => `${n} people`,
      aggSuffix: 'on the move',
      near: (venue: string) => `near ${venue}`,
      venueSep: ', ',
      othersAfter: (venues: string) => `${venues} and more`,
    },
    login: {
      trigger: 'Sign in',
      title: 'Sign in',
      desc: 'Sign in with Google to post your location and posts from the web. No sign-in needed just to browse.',
      google: 'Continue with Google',
    },
    menu: { logout: 'Sign out', trackingBadge: 'Sharing location (tracking)' },
    tracking: {
      trigger: '📡 Tracking',
      triggerActive: '📡 Tracking',
      title: 'Tracking',
      note: 'While this page is open, sends your location every 30 seconds to move your pin. Closing the screen or switching apps pauses it (it resumes when you return). Uses more battery.',
      durationLabel: 'Share for',
      durations: {
        '15m': '15 minutes',
        '1h': '1 hour',
        '8h': '8 hours',
        indefinite: 'Until I stop',
      },
      start: 'Start',
      stop: 'Stop',
      sharing: (remaining: string) => `Sharing location (${remaining})`,
      indefinite: 'until stopped',
      remaining: (sec: number) => {
        const min = Math.floor(sec / 60);
        if (min < 1) {
          return 'ending soon';
        }
        const h = Math.floor(min / 60);
        return h > 0 ? `${h}h ${min % 60}m left` : `${min}m left`;
      },
      lastSent: (time: string) => `Last sent: ${time}`,
      notSentYet: 'Getting your location…',
      needPermission:
        'Tracking needs location access. Click the icon at the left of the address bar → "Site settings" → set "Location" to "Allow". "Start" becomes available once allowed.',
      unsupported: 'Tracking is not available on this device or browser.',
      checking: 'Checking your location…',
      startFailed: {
        denied: 'Could not start because location access was not allowed.',
        unavailable:
          "Could not start because your location couldn't be determined. Make sure location services are on.",
        timeout: 'Could not start because getting your location timed out. Please try again.',
      },
      hiddenWarning:
        '"Show on map" is off, so your location will not appear on the map (change it in Check in).',
      problem: {
        denied:
          'Stopped because location access is not allowed. Allow it in your browser site settings, then start again.',
        session: 'Stopped because your session expired. Please sign in again and restart.',
        network: 'Sending failed. Retrying at the next interval.',
        position: 'Could not get your location yet. Being outdoors or near a window may help.',
      },
    },
    checkin: {
      trigger: '📍 Check in',
      title: 'Check in',
      here: 'Here (current location)',
      fromVenue: 'Pick a venue',
      showOnMap: 'Show on map',
      showTrail: 'Show trail',
      trailNeedsMap: 'Trail can be set only while "Show on map" is on.',
      locating: 'Getting your location…',
      loadingSettings: 'Loading visibility settings…',
      settingsFailed: 'Could not load visibility settings. Please reopen this dialog.',
      done: 'Checked in! Your pin will appear on the map.',
      doneHidden: 'Checked in (not shown on the map because "Show on map" is off).',
      failed: 'Check-in failed. Please try again later.',
      sessionExpired: 'Your session has expired. Please sign in again.',
      noGeo: 'This device cannot get your location. Please pick a venue.',
      denied:
        'Location access is not allowed. Check your browser permissions or pick a venue.',
      timeout:
        'Getting your location timed out. Please try again or pick a venue.',
      geoFailed: 'Could not get your location. Please pick a venue.',
    },
    profile: {
      trigger: '⚙️ Profile',
      title: 'Edit profile',
      displayName: 'Display name',
      namePlaceholder: 'Name shown on the map',
      hideAvatar: 'Hide icon (photo)',
      avatarNote:
        'Only your Google image can be used (no upload). Hiding it shows an initial circle.',
      save: 'Save',
      reset: 'Reset to default',
      sessionExpired: 'Your session has expired. Please sign in again.',
      nameEmpty: 'Please enter a display name.',
      nameTooLong: (max: number) =>
        `Display name must be ${max} characters or fewer.`,
      saved: 'Profile updated. It will show on the map.',
      saveFailed: 'Update failed. Please try again later.',
      resetDone: 'Reset to default (sign-in name and image).',
      resetFailed: 'Reset failed. Please try again later.',
    },
    status: {
      trigger: '💬 Status',
      title: 'Status',
      note: 'Shows a short message above your avatar on the map (auto-clears after 3 hours).',
      placeholder: 'e.g. Having lunch / Waiting for a session',
      show: 'Show',
      clear: 'Clear',
      tooLong: (max: number) => `Status must be ${max} characters or fewer.`,
      sessionExpired: 'Your session has expired. Please sign in again.',
      updated: 'Status updated. It appears above your avatar on the map.',
      loading: 'Loading…',
      remaining: (sec: number) => {
        const min = Math.floor(sec / 60);
        if (min < 1) {
          return 'Clears soon';
        }
        const h = Math.floor(min / 60);
        return h > 0 ? `Clears in ${h}h ${min % 60}m` : `Clears in ${min}m`;
      },
      cleared: 'Status cleared.',
      failed: 'Update failed. Please try again later.',
      needCheckin: 'Please check in first to set a status.',
    },
    post: {
      trigger: '📝 Post',
      title: 'Create a post',
      note: 'Drops a pin where you are now (or at your check-in from the last 60 minutes if your location is unavailable). Add text, image, or video (up to 150MB).',
      bodyPlaceholder: 'Say something about now',
      addImage: 'Add image',
      reselectImage: 'Change image',
      addVideo: 'Add video',
      reselectVideo: 'Change video',
      sizeLimit: 'up to 150MB',
      removeImage: 'Remove image',
      removeVideo: 'Remove video',
      submit: 'Post',
      sessionExpired: 'Your session has expired. Please sign in again.',
      empty: 'Please enter text, an image, or a video.',
      bodyTooLong: (max: number) => `Text must be ${max} characters or fewer.`,
      videoTooLarge:
        'Video is too large (max 150MB). If you shot it in 4K, trim it or record in 1080p.',
      locating: 'Getting your location…',
      postAtCurrent: '📍 Posting at your current location',
      postAtCurrentNear: (venue: string) =>
        `📍 Posting at your current location (near ${venue})`,
      postAtCheckin: '📍 Posting at your latest check-in location',
      postAtCheckinNear: (venue: string) =>
        `📍 Posting at your latest check-in location (near ${venue})`,
      imageLoadFailed: 'Could not load the image. Please try another one.',
      uploading: 'Uploading video…',
      posted: 'Posted! A pin was added to the map.',
      needLocation:
        'Posting needs your current location or a check-in from the last 60 minutes. Tap "Retry location" or check in first.',
      locDenied:
        'Location access is blocked. Click the icon at the left of the address bar → "Site settings" → set "Location" to "Allow". It retries automatically once allowed (reload the page if it does not).',
      locStillDenied: 'Location is still blocked. Allow it in your site settings first.',
      locRetryFailed: 'Tried again, but could not get your location.',
      locRetrying: 'Getting location…',
      reloadPage: 'Reload page',
      reloadNote: 'Reloading clears the text, image, and video you have entered.',
      locUnavailable:
        "Couldn't determine your location. Make sure your device's location services are on.",
      locTimeout: 'Getting your location timed out. Please try again.',
      locUnsupported: 'This device cannot get your location.',
      retryLocation: 'Retry location',
      failed: 'Posting failed. Please try again later.',
    },
    postPopup: {
      imageAlt: 'Post image',
      delete: 'Delete this post',
      backToList: '← Back to posts here',
      confirmDelete: 'Delete this post? This cannot be undone.',
      spotTitle: (n: number) => `${n} posts here`,
      shareX: 'Share on X',
      shareFacebook: 'Share on Facebook',
      shareLinkedin: 'Share on LinkedIn',
      shareNative: 'Share via…',
      authorSuffix: (name: string) => `${name}'s post`,
      prevPage: 'Prev',
      nextPage: 'Next',
      pageOf: (cur: number, total: number) => `${cur} / ${total}`,
    },
    time: {
      justNow: 'just now',
      minutesAgo: (n: number) => `${n}m ago`,
      hoursAgo: (n: number) => `${n}h ago`,
      daysAgo: (n: number) => `${n}d ago`,
    },
  },
} as const;

/** 現在言語の文言面を返すショートカット。 */
export function messages(lang: Lang) {
  return MESSAGES[lang];
}
